import { useAccount, useBlock, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import auctionAbi from '@/lib/abis/HorizonAuction.json'
import { POLL_FAST } from '@/lib/utils'

export const AUCTION_ADDR = ADDRESSES.HORIZON_AUCTION
const isSet = (a) => !!a && a.toLowerCase() !== ZERO_ADDR.toLowerCase()
export const AUCTION_LIVE = IS_LIVE && isSet(AUCTION_ADDR)
const LOOKBACK = 14 // epochs of history to scan for the user's claimable/ended entries

/// Config + the current epoch's pool + the connected user's recent auction entries (HorizonAuction). The
/// live per-amount "power" (USD value of a hypothetical deposit) is read in the form itself, keyed on what
/// the user is typing; this hook is the steady state.
///
/// `oracleSet` is the launch gate: the auction reverts `OracleNotSet` on `enter` until genesis wires the
/// input oracle at go-live. Unlike the retired mint it does NOT need the HORIZON/X28 pool (it never prices
/// HORIZON), so it can go live with feeds that already exist.
export function useHorizonAuction() {
  const { address: user } = useAccount()

  // The chain's own clock, so the epoch countdown measures the on-chain deadline against block time
  // rather than the browser wall clock (the two diverge on a dev fork whose time has been advanced).
  const { data: block } = useBlock({ watch: true, query: { enabled: AUCTION_LIVE } })
  const chainNow = block?.timestamp != null ? Number(block.timestamp) : undefined

  // Phase 1 — config + the current epoch index.
  const { data: cfg, refetch: refetchCfg } = useReadContracts({
    query: { enabled: AUCTION_LIVE, refetchInterval: POLL_FAST },
    contracts: [
      { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'currentEpoch' },
      { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'oracle' },
      { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'auctionBps' },
      { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'paused' },
    ],
  })
  const epoch = cfg?.[0]?.result ?? 0n
  const oracle = cfg?.[1]?.result ?? ZERO_ADDR
  const oracleSet = isSet(oracle)
  const auctionBps = Number(cfg?.[2]?.result ?? 0n)
  const paused = cfg?.[3]?.result ?? false

  // Phase 2 — the current epoch's pool, plus a window of epochs for the user's positions.
  const lo = epoch > BigInt(LOOKBACK) ? epoch - BigInt(LOOKBACK) : 0n
  const scan = []
  for (let e = lo; e <= epoch; e++) scan.push(e)

  const poolContracts = [
    { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'allotmentFor', args: [epoch] },
    { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'totalUsd', args: [epoch] },
    { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'epochEnd', args: [epoch] },
  ]
  const userContracts = user ? scan.flatMap((e) => ([
    { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'userUsd', args: [user, e] },
    { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'previewClaim', args: [user, e] },
    { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'claimed', args: [user, e] },
    { address: AUCTION_ADDR, abi: auctionAbi, functionName: 'isSettled', args: [e] },
  ])) : []

  const { data: d2, refetch: refetchPool } = useReadContracts({
    query: { enabled: AUCTION_LIVE && !!cfg, refetchInterval: POLL_FAST },
    contracts: [...poolContracts, ...userContracts],
  })

  const allot = d2?.[0]?.result ?? 0n
  const totalUsd = d2?.[1]?.result ?? 0n
  const epochEnd = Number(d2?.[2]?.result ?? 0n)

  const positions = []
  if (user) {
    for (let i = 0; i < scan.length; i++) {
      const base = 3 + i * 4 // after the 3 pool reads
      const uUsd = d2?.[base]?.result ?? 0n
      if (uUsd === 0n) continue // did not participate this epoch
      positions.push({
        epoch: scan[i],
        userUsd: uUsd,
        horizon: d2?.[base + 1]?.result ?? 0n, // previewClaim: final if settled, live estimate if current
        claimed: d2?.[base + 2]?.result ?? false,
        settled: d2?.[base + 3]?.result ?? false,
      })
    }
  }
  // Claimable = a settled epoch, not yet claimed, with a positive share.
  const claimable = positions.filter((p) => p.settled && !p.claimed && p.horizon > 0n)
  const claimableTotal = claimable.reduce((s, p) => s + p.horizon, 0n)

  const refetch = () => { refetchCfg?.(); refetchPool?.() }
  return { auctionLive: AUCTION_LIVE, oracleSet, paused, epoch, allot, totalUsd, epochEnd, chainNow, auctionBps, positions, claimable, claimableTotal, refetch }
}
